import React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Dropdown, Tooltip, Popover } from 'antd';
import { signOut } from '../../helpers/auth';
import { logOut, setViewMode } from '../../ducks/Auth/actions';
import { clearProfile } from '../../ducks/Profile/actions';
import { clearRecipes } from '../../ducks/Recipe/actions';
import { hasMinimumRole } from '../../utils/permissions';
import { resolveViewMode, canUseClientView } from '../../utils/viewMode';
import OnboardingChecklist from './OnboardingChecklist';
import { startTrainerTour } from '../TrainerTour';
import {
  IcoHome,
  IcoApple,
  IcoDumb,
  IcoUsers,
  IcoLib,
  IcoTrend,
  IcoTools,
  IcoAcademic,
  IcoMsg,
  IcoMore,
  IcoChevR,
  IcoPlus,
  IcoBook,
  IcoBolt,
} from './icons';
// Wordmark variants — light/dark swap is handled via CSS (`.dark`
// selector). Both PNGs are imported so the bundler hashes them.
import NubodWordmarkBlack from '../../assets/logos/nubod-black.png';
import NubodWordmarkWhite from '../../assets/logos/nubod-white.png';

/**
 * Primary navigation. Replaces src/components/SideBar.
 *
 * Preserves every behavior the old sidebar had:
 *   - Role-based menu generation (client / trainer / org-admin+ )
 *   - Conditional Workout Program for clients with `user.createdBy`
 *     (must be assigned to a trainer to see programs)
 *   - Conditional Library for trainer+ only
 *   - Conditional Clients for organization_admin+
 *   - Conditional Billing under Settings (hidden when userType === 'fixed')
 *   - Logout flow: signOut + Redux clear + redirect to /
 *
 * Sub-navigation: each section that defines `subMenuItems` renders as a
 * collapsible group. The section containing the active route auto-expands;
 * other sections stay collapsed. Clicking a parent section navigates to
 * its default page AND opens the group; the chevron lets the user
 * collapse or pop a different group open without navigating away. This
 * replaces the in-page <SubMenu /> as the source of truth for child
 * navigation.
 */
class Sidebar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      userMenuOpen: false,
      createMenuOpen: false,
      // Single-section accordion. Three values:
      //   null   — auto-expand the section that contains the active route
      //   'none' — user collapsed the auto-expanded section
      //   <key>  — user explicitly opened that section (e.g. peeking at
      //            Tools while still browsing within Nutrition)
      // Resets to null on route change so navigating into a new section
      // auto-expands it.
      expandedKey: null,
      // True once the trainer/org branding logo fails to load — flips the
      // brand slot to the Nubod wordmark instead of a broken-image icon.
      brandLogoFailed: false,
    };
    this.handleDocClick = this.handleDocClick.bind(this);
  }

  componentDidMount() {
    document.addEventListener('mousedown', this.handleDocClick);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location?.pathname !== this.props.location?.pathname) {
      // Reset the accordion to auto so the section containing the new
      // route auto-expands (and any peek-open section collapses).
      if (this.state.expandedKey !== null) {
        this.setState({ expandedKey: null });
      }
    }
    // A new/fixed logo URL deserves a fresh load attempt — clear the failed flag
    // so a replaced logo isn't stuck on the wordmark fallback.
    if (
      prevProps.auth?.user?.branding?.logo !==
        this.props.auth?.user?.branding?.logo &&
      this.state.brandLogoFailed
    ) {
      this.setState({ brandLogoFailed: false });
    }
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleDocClick);
  }

  handleDocClick(e) {
    // Close the user-card menu when clicking outside the user-card region.
    if (
      this.state.userMenuOpen &&
      this.userCardRef &&
      !this.userCardRef.contains(e.target)
    ) {
      this.setState({ userMenuOpen: false });
    }
  }

  navItemsForUser() {
    const { auth } = this.props;
    const user = auth?.user || {};
    const role = user.role || 'client';
    const isTrainerPlus = hasMinimumRole(role, 'trainer');
    const isFixedUserType = user.userType === 'fixed';
    // "View as client" is gated on the server `canSelfTrack` flag — resolved on
    // /api/auth from the AppConfig `trainerSelfTracking` mode + role. With the
    // flag at 'admins' this is platform-admins only (unchanged); flipping it to
    // 'trainers' opens client view to all coaches with no code change.
    // resolveViewMode also neutralizes a stale `viewMode === 'client'` from a
    // prior session so it can't lock a user out of the trainer view.
    const viewMode = resolveViewMode(user, auth?.viewMode);
    const inClientMode = viewMode === 'client';
    const isClient = !isTrainerPlus || inClientMode;
    const hasCreatedBy = !!user.createdBy;
    // Nutrition module: hide all nutrition nav + tools for a client whose trainer
    // disabled nutrition. Absent ⇒ enabled (backward-compatible).
    const nutritionOn = user?.enabledModules?.nutrition !== false;

    // Challenge-only clients (enrolled into a challenge but not a full 1:1
    // client) get a stripped app: the coaching surfaces — assigned Workout Plan
    // + Program — are hidden; their challenge lives on the Home banner. Raw
    // boolean off /api/auth (default false), so trainers + full clients are
    // unaffected.
    const challengeOnly = user?.challengeOnly === true;

    // The client's Challenges tab is shown only when they're actually in a
    // challenge (invited/active) — a `hasChallenges` flag resolved on /api/auth
    // — so it doesn't clutter the majority who aren't in one. (When self-signup
    // / Discover lands, this can broaden to "can join one".)
    const hasChallenges = user?.hasChallenges === true;

    // Names + ordering match the legacy `src/components/SideBar` exactly.
    // `subMenuItems` is now consumed in-place by `renderSection()` —
    // the legacy `<SubMenu />` + `setCurrentPage` plumbing is gone.

    // ---- Shared sub-menu definitions (kept in sync with legacy) -----------

    const nutritionSub = [
      { name: 'Meal Plan', path: '/dashboard/nutrition/meal-plan' },
      { name: 'Recipes', path: '/dashboard/nutrition/recipes' },
      { name: 'Shopping List', path: '/dashboard/nutrition/shopping-list' },
      { name: 'Food Preferences', path: '/dashboard/nutrition/food-preferences' },
    ];

    const workoutSub = [
      // Assigned Plan is a 1:1 coaching surface — hidden for challenge-only.
      ...(challengeOnly
        ? []
        : [{ name: 'Workout Plan', path: '/dashboard/workout/workout-plans' }]),
      // Workout Program is only meaningful when the client was set up by a
      // trainer — same condition as the legacy sidebar (also hidden for
      // challenge-only clients).
      ...(challengeOnly || (isClient && !hasCreatedBy)
        ? []
        : [{ name: 'Workout Program', path: '/dashboard/workout/workout-program' }]),
      { name: 'Workouts', path: '/dashboard/workout/workouts' },
      // When nutrition is off the whole Tools tab is dropped (every other Tools
      // item is nutrition). One Rep Max is a strength tool, so surface it here
      // under Workout rather than orphaning it to a URL-only route — EXCEPT for
      // challenge-only invitees, who get the stripped experience with no
      // self-serve tools at all (their home is the challenge).
      ...(nutritionOn || challengeOnly
        ? []
        : [{ name: 'One Rep Max', path: '/dashboard/tools/one-rep-max' }]),
    ];

    const toolsSub = [
      { name: 'Macro Calculator', path: '/dashboard/tools/macro-calculator' },
      { name: 'One Rep Max', path: '/dashboard/tools/one-rep-max' },
      { name: 'Carb Cycling', path: '/dashboard/tools/carb-cycling' },
      { name: 'Macro History', path: '/dashboard/tools/macro-history' },
      // "Macro Compliance" reads Fitbit nutrition data (legacy). The new
      // "Food Compliance" tab below reads the mobile app / web food log.
      // Keep both during the Fitbit sunset window.
      { name: 'Macro Compliance', path: '/dashboard/tools/compliance' },
      { name: 'Food Compliance', path: '/dashboard/tools/food-compliance' },
    ];
    // The Tools tab is entirely nutrition apart from One Rep Max, which moves
    // under Workout when nutrition is off — so the tab is only shown at all when
    // nutrition is on (see the `nutritionOn` guard on the `tools` item below).

    const statsSub = [
      { name: 'Progress', path: '/dashboard/stats/progress' },
      { name: 'Exercise History', path: '/dashboard/stats/exercise-history' },
      { name: 'Check-in History', path: '/dashboard/check-ins' },
    ];

    const librarySub = [
      { name: 'Workouts', path: '/dashboard/admin/library/workouts' },
      { name: 'Workout Plans', path: '/dashboard/admin/library/workout-plans' },
      { name: 'Workout Programs', path: '/dashboard/admin/library/workout-program' },
      { name: 'Exercises', path: '/dashboard/admin/library/exercises' },
      { name: 'Meal Plans', path: '/dashboard/admin/library/meal-plan' },
      { name: 'Assessments', path: '/dashboard/admin/assessments' },
    ];

    const settingsSub = [
      { name: 'Account', path: '/dashboard/settings' },
      { name: 'Integrations', path: '/dashboard/settings/integrations' },
      // Billing temporarily hidden from the nav while the surface
      // gets a V2 pass; the route still resolves (settings.js routes
      // /billing → SubscriptionPage) so existing deep-links and
      // expired-subscription redirects keep working.
    ];

    // ---- Build per-role link list ---------------------------------------

    if (isClient) {
      const items = [
        { key: 'home', label: 'Home', icon: IcoHome, path: '/dashboard/home', subMenuItems: [] },
        ...(nutritionOn
          ? [
              {
                key: 'nutrition',
                label: 'Nutrition',
                icon: IcoApple,
                path: '/dashboard/nutrition/meal-plan',
                subMenuItems: nutritionSub,
              },
            ]
          : []),
        {
          key: 'workout',
          label: 'Workout',
          icon: IcoDumb,
          // Challenge-only clients have no assigned Plan page — land the parent
          // row on their logged Workouts instead.
          path: challengeOnly
            ? '/dashboard/workout/workouts'
            : '/dashboard/workout/workout-plans',
          subMenuItems: workoutSub,
        },
        // Challenges tab — only when the client is in a challenge. Its own home
        // for challenges (workouts + leaderboard + wall), separate from the
        // 1:1 Workout Plan surface.
        ...(hasChallenges
          ? [
              {
                key: 'challenges',
                label: 'Challenges',
                icon: IcoBolt,
                path: '/dashboard/challenges',
                subMenuItems: [],
              },
            ]
          : []),
        // Tools (Macro Calculator, One Rep Max, Carb Cycling, Macro
        // History/Compliance, Food Compliance) are self-serve optimization
        // surfaces that need assessment/macro data. Hidden when nutrition is
        // off, and entirely for challenge-only invitees — their focus is the
        // challenge, not macro tinkering, and most of these are empty/broken
        // without an assessment.
        ...(nutritionOn && !challengeOnly
          ? [
              {
                key: 'tools',
                label: 'Tools',
                icon: IcoTools,
                path: '/dashboard/tools/macro-calculator',
                subMenuItems: toolsSub,
              },
            ]
          : []),
        {
          key: 'stats',
          label: 'Stats',
          icon: IcoTrend,
          path: '/dashboard/stats/progress',
          subMenuItems: statsSub,
        },
      ];
      // Settings deliberately omitted from the main sidebar — the
      // user-avatar dropdown at the bottom of the rail is the single
      // entry point for personal/account configuration (Account,
      // Integrations, Sign out). Matches the Slack/Linear/Stripe
      // pattern where the main nav surfaces product work and the
      // avatar handles "about you."
      return { primary: items, settings: null, hasCreatedBy };
    }

    // Trainer + above (in trainer view-mode). Ordering: Home,
    // Clients (trainer+), Trainers (org-admin+), Library (trainer+).
    // The Home item points at the Studio Overview (/dashboard/admin)
    // — that's the trainer's actual landing. Flipping the toggle to client mode
    // falls through to the `if (isClient)` branch above, where Home
    // points at /dashboard/home and the client-facing tabs reappear.
    //
    // Nutrition, Workout, Tools, and Stats are intentionally
    // OMITTED here — they're all personal-data surfaces (assigned
    // meal plan, assigned workout, macro calculator, and your own
    // progress charts). Trainers don't have personal plans
    // or stats; they manage clients' plans via Library and view
    // client stats via the Clients drilldown. Carried over from an
    // earlier shared sidebar, keeping them just routed trainers to
    // empty-state screens for their own user.
    const items = [
      { key: 'home', label: 'Home', icon: IcoHome, path: '/dashboard/admin', subMenuItems: [] },
      // Clients — core trainer surface. The underlying route accepts
      // `requireRole='trainer'`, so every trainer-plus role sees it.
      {
        key: 'clients',
        label: 'Clients',
        icon: IcoUsers,
        path: '/dashboard/admin/clients',
        subMenuItems: [],
      },
    ];
    // Trainers — org/platform admins only. A plain trainer manages clients,
    // never other trainers, and its route + API both require
    // `organization_admin`, so showing it to them would only lead to
    // /unauthorized. Pushed here (between Clients and Library) because array
    // order is render order.
    //
    // IcoAcademic, not IcoUsers: Clients sits directly above and already
    // owns IcoUsers, so sharing the glyph would make two adjacent rail
    // items indistinguishable at a glance.
    if (hasMinimumRole(role, 'organization_admin')) {
      items.push({
        key: 'trainers',
        label: 'Trainers',
        icon: IcoAcademic,
        path: '/dashboard/admin/trainers',
        subMenuItems: [],
      });
    }
    items.push({
      key: 'library',
      label: 'Library',
      icon: IcoLib,
      path: '/dashboard/admin/library/workouts',
      subMenuItems: librarySub,
    });
    // Challenges — group runs built on a library plan. SOFT-LAUNCH: gated to
    // platform_admin (super admin) only while the feature rolls out, so regular
    // trainers/org-admins don't see it yet. The IndexRoute.js route guards are
    // gated to match, so it's hidden AND unreachable by direct URL. Widen both
    // back to `trainer` to open it to every coach. IcoBolt, distinct from IcoLib.
    if (hasMinimumRole(role, 'platform_admin')) {
      items.push({
        key: 'challenges',
        label: 'Challenges',
        icon: IcoBolt,
        path: '/dashboard/admin/challenges',
        subMenuItems: [],
      });
    }
    // Settings deliberately omitted (see client-branch note above).
    // The trainer's avatar dropdown at the bottom of the rail is the
    // single entry point for account configuration.
    return { primary: items, settings: null, hasCreatedBy: false };
  }

  isActive(path) {
    const here = this.props.location?.pathname || '';
    if (here === path) return true;
    // Section-prefix match so any deep link inside the section keeps the
    // parent highlighted AND keeps its accordion group open.
    //   /dashboard/tools/macro-calculator → prefix `/dashboard/tools`
    //   /dashboard/admin/library/workouts → prefix `/dashboard/admin/library`
    // Without the admin special-case, `/dashboard/admin/clients` would
    // also match `library`.
    const segs = path.split('/');
    const isAdmin = segs[2] === 'admin';
    const prefixLen = isAdmin ? 4 : 3;
    if (segs.length < prefixLen) return false;
    const prefix = segs.slice(0, prefixLen).join('/');
    return here === prefix || here.startsWith(prefix + '/');
  }

  // Exact-match check for child links. Settings tabs use a stricter match
  // because /dashboard/settings is a parent of /dashboard/settings/billing
  // — a startsWith would mark Account as active everywhere.
  isChildActive(path) {
    const here = this.props.location?.pathname || '';
    if (path === '/dashboard/settings') {
      return here === '/dashboard/settings';
    }
    // Strip query strings before comparing — query params don't
    // change the route match.
    const cleanPath = path.split('?')[0];
    return here === cleanPath || here.startsWith(`${cleanPath}/`);
  }

  // True when the active route lives inside the section. Covers two
  // cases: (1) the section's own path prefix (e.g. /dashboard/tools)
  // matches the current location, OR (2) any child route in subMenuItems
  // matches — needed because some children break the prefix (e.g.
  // Library → Assessments routes to /dashboard/admin/assessments,
  // outside the /dashboard/admin/library prefix).
  containsActiveRoute(item) {
    if (this.isActive(item.path)) return true;
    if (item.subMenuItems && item.subMenuItems.length > 0) {
      return item.subMenuItems.some((child) =>
        this.isChildActive(child.path)
      );
    }
    return false;
  }

  // Single-section accordion: at most one section is open at a time.
  // Default state opens the section containing the active route; a
  // chevron click overrides to a specific section (or 'none' to collapse
  // everything).
  isSectionOpen(item) {
    if (!item.subMenuItems || item.subMenuItems.length === 0) return false;
    const { expandedKey } = this.state;
    if (expandedKey === null) return this.containsActiveRoute(item);
    if (expandedKey === 'none') return false;
    return expandedKey === item.key;
  }

  toggleSection = (key) => (e) => {
    e.stopPropagation();
    this.setState((prev) => {
      const item = this.findItemByKey(key);
      const isCurrentlyOpen =
        prev.expandedKey === key ||
        (prev.expandedKey === null && item && this.containsActiveRoute(item));
      return { expandedKey: isCurrentlyOpen ? 'none' : key };
    });
  };

  findItemByKey(key) {
    const { primary, settings } = this.navItemsForUser();
    const all = settings ? [...primary, settings] : primary;
    return all.find((it) => it.key === key);
  }

  navigate(path) {
    if (this.props.history) {
      this.props.history.push(path);
    }
  }

  // Quick-create menu (the "+ Create" button above the Coach nav). Each item
  // routes into the EXISTING create flow: workout/exercise/program land on
  // their library page with `?new=1`, which auto-opens that page's create
  // modal (handled in each page's componentDidMount); meal plan goes straight
  // to the fresh V2 builder.
  handleQuickCreate = (key) => {
    const routes = {
      // Reuses the clients page's existing `?invite=1` handler.
      client: '/dashboard/admin/clients?invite=1',
      workout: '/dashboard/admin/library/workouts?new=1',
      program: '/dashboard/admin/library/workout-program?new=1',
      mealplan: '/dashboard/admin/library/meal-plan-v2',
      exercise: '/dashboard/admin/library/exercises?new=1',
    };
    if (routes[key]) this.navigate(routes[key]);
  };

  handleLogout = async () => {
    try {
      await signOut();
    } catch (_) {
      /* fall through */
    }
    this.props.clearProfile?.();
    this.props.clearRecipes?.();
    this.props.logOut?.();
    if (this.props.history) this.props.history.push('/');
  };

  // The account menu items (Profile / Account / Integrations / Sign out),
  // reused as the antd Dropdown overlay in the collapsed rail.
  accountOverlay() {
    const { auth } = this.props;
    const isTrainerPlus = hasMinimumRole(auth?.user?.role || 'client', 'trainer');
    return (
      <div className="nav-create-menu" style={{ minWidth: 200 }}>
        {!isTrainerPlus ? (
          <button type="button" className="nav-create-item" onClick={() => this.navigate('/dashboard/profile')}>
            <span>Profile</span>
          </button>
        ) : null}
        <button type="button" className="nav-create-item" onClick={() => this.navigate('/dashboard/settings')}>
          <span>Account settings</span>
        </button>
        <button type="button" className="nav-create-item" onClick={() => this.navigate('/dashboard/settings/integrations')}>
          <span>Integrations</span>
        </button>
        <div className="nav-create-sep" />
        {isTrainerPlus ? (
          <button type="button" className="nav-create-item" onClick={startTrainerTour}>
            <span>Take the tour</span>
          </button>
        ) : null}
        <button type="button" className="nav-create-item" style={{ color: 'var(--error)' }} onClick={this.handleLogout}>
          <span>Sign out</span>
        </button>
      </div>
    );
  }

  renderUserCard() {
    const { auth } = this.props;
    const user = auth?.user || {};
    const role = user.role || 'client';
    const isTrainerPlus = hasMinimumRole(role, 'trainer');
    const isFixed = user.userType === 'fixed';
    const initial = (user.name?.[0] || user.email?.[0] || '?').toUpperCase();
    const fullName =
      [user.name, user.lastName].filter(Boolean).join(' ') ||
      user.email ||
      'You';
    const tag = isTrainerPlus ? 'Trainer' : 'Pro';

    // Collapsed rail: just the avatar, with the account menu as a portaled
    // antd Dropdown (the custom absolute menu would be clipped by the rail).
    if (this.props.collapsed) {
      return (
        <Dropdown trigger={['click']} placement="topLeft" overlay={this.accountOverlay()}>
          <div className="user-card" role="button" tabIndex={0} aria-label="Account menu" title={fullName} data-tour="account">
            <div className="av">
              {user.avatar ? <img src={user.avatar} alt={fullName} /> : initial}
            </div>
          </div>
        </Dropdown>
      );
    }

    return (
      <div
        className="user-card"
        data-tour="account"
        ref={(el) => {
          this.userCardRef = el;
        }}
      >
        <div className="av">
          {user.avatar ? <img src={user.avatar} alt={fullName} /> : initial}
        </div>
        <div style={{ minWidth: 0 }}>
          <div className="name" title={fullName}>
            {fullName}
          </div>
          <div className="role">{tag}</div>
        </div>
        <button
          className="more"
          aria-label="Account menu"
          onClick={() =>
            this.setState((s) => ({ userMenuOpen: !s.userMenuOpen }))
          }
        >
          <IcoMore size={16} />
        </button>
        {this.state.userMenuOpen ? (
          <div
            style={{
              position: 'absolute',
              right: 10,
              bottom: 64,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
              borderRadius: 12,
              boxShadow: 'var(--shadow-pop)',
              minWidth: 200,
              padding: 6,
              zIndex: 20,
            }}
          >
            {/* /dashboard/profile renders the client's assessment
                + macro data. Trainer-plus roles don't have a Profile
                doc (it's a client-only collection), so the page just
                spins forever for them. Their "profile" equivalent
                lives under Account settings → Business profile. */}
            {!isTrainerPlus && (
              <button
                onClick={() => {
                  this.setState({ userMenuOpen: false });
                  this.navigate('/dashboard/profile');
                }}
                style={menuItemStyle}
              >
                Profile
              </button>
            )}
            <button
              onClick={() => {
                this.setState({ userMenuOpen: false });
                this.navigate('/dashboard/settings');
              }}
              style={menuItemStyle}
            >
              Account settings
            </button>
            <button
              onClick={() => {
                this.setState({ userMenuOpen: false });
                this.navigate('/dashboard/settings/integrations');
              }}
              style={menuItemStyle}
            >
              Integrations
            </button>
            {isTrainerPlus ? (
              <button
                onClick={() => {
                  this.setState({ userMenuOpen: false });
                  startTrainerTour();
                }}
                style={menuItemStyle}
              >
                Take the tour
              </button>
            ) : null}
            {/* Billing entry intentionally hidden — route still
                resolves; bring this button back once the V2 billing
                surface lands. */}
            <div
              style={{
                height: 1,
                background: 'var(--border-soft)',
                margin: '4px 0',
              }}
            />
            <button
              onClick={() => {
                this.setState({ userMenuOpen: false });
                this.handleLogout();
              }}
              style={{ ...menuItemStyle, color: 'var(--error)' }}
            >
              Sign out
            </button>
          </div>
        ) : null}
      </div>
    );
  }

  // Parent section with optional nested children. Clicking the row
  // navigates to the section's default path AND opens the group. The
  // chevron is a separate hit target that toggles open/closed without
  // navigating — useful for peeking at another section's children
  // while staying on the current page.
  // Collapsed "icon rail": a leaf item gets an antd Tooltip (label on hover);
  // a section with children gets an antd Dropdown whose overlay is the flyout of
  // children (both PORTAL to <body>, so they escape the sidebar's overflow clip).
  renderRailSection(item) {
    const hasChildren = item.subMenuItems && item.subMenuItems.length > 0;
    const isActive = this.containsActiveRoute(item);
    const iconBtn = (
      <div className="nav-group">
        <button
          type="button"
          className={`nav-item ${isActive ? 'active' : ''}`}
          onClick={() => this.navigate(item.path)}
          aria-label={item.label}
          data-tour={item.key}
        >
          <item.icon className="ico" size={20} />
          <span className="nav-item-label">{item.label}</span>
        </button>
      </div>
    );
    if (hasChildren) {
      return (
        <Popover
          key={item.key}
          trigger="hover"
          placement="rightTop"
          overlayClassName="nav-flyout-pop"
          content={
            <div className="nav-flyout">
              <div className="nav-flyout-title">{item.label}</div>
              {item.subMenuItems.map((child) => (
                <button
                  key={child.path}
                  type="button"
                  className={`nav-flyout-item ${
                    this.isChildActive(child.path) ? 'active' : ''
                  }`}
                  onClick={() => this.navigate(child.path)}
                >
                  {child.name}
                </button>
              ))}
            </div>
          }
        >
          {iconBtn}
        </Popover>
      );
    }
    return (
      <Tooltip key={item.key} placement="right" title={item.label}>
        {iconBtn}
      </Tooltip>
    );
  }

  renderSection(item) {
    if (this.props.collapsed) return this.renderRailSection(item);
    const hasChildren = item.subMenuItems && item.subMenuItems.length > 0;
    // Highlight the parent row when the user is anywhere inside the
    // section — including child paths that break the parent's prefix
    // (e.g. Library → Assessments).
    const isActive = this.containsActiveRoute(item);
    const isOpen = hasChildren && this.isSectionOpen(item);
    return (
      <div key={item.key} className="nav-group">
        <button
          type="button"
          className={`nav-item ${isActive ? 'active' : ''}`}
          data-tour={item.key}
          onClick={() => {
            this.navigate(item.path);
            if (hasChildren) {
              // After navigating, force this section open and collapse
              // any peek-open one. componentDidUpdate's route-change
              // reset would set expandedKey to null and the active
              // section would auto-expand anyway, but setting it
              // explicitly avoids a one-frame flicker.
              this.setState({ expandedKey: item.key });
            }
          }}
        >
          <item.icon className="ico" size={18} />
          <span className="nav-item-label">{item.label}</span>
          {item.badge ? <span className="badge">{item.badge}</span> : null}
          {hasChildren ? (
            <span
              className="nav-chevron"
              role="button"
              tabIndex={0}
              aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${item.label}`}
              aria-expanded={isOpen}
              onClick={this.toggleSection(item.key)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  this.toggleSection(item.key)(e);
                }
              }}
              style={{
                transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
              }}
            >
              <IcoChevR size={12} />
            </span>
          ) : null}
        </button>
        {hasChildren && isOpen ? (
          <div className="nav-children" role="group">
            {item.subMenuItems.map((child) => (
              <button
                type="button"
                key={child.path}
                className={`nav-child ${
                  this.isChildActive(child.path) ? 'active' : ''
                }`}
                onClick={() => this.navigate(child.path)}
              >
                <span className="nav-child-dot" aria-hidden="true" />
                <span className="nav-child-label">{child.name}</span>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  }

  // Flip the trainer view-mode toggle. Persists the choice (the action
  // creator writes to localStorage) and navigates to the appropriate
  // landing so the user isn't stranded on a page that doesn't belong
  // to their new mode.
  handleToggleViewMode = (next) => {
    if (!this.props.setViewMode) return;
    this.props.setViewMode(next);
    this.props.history?.push(
      next === 'client' ? '/dashboard/home' : '/dashboard/admin',
    );
  };

  render() {
    const { auth } = this.props;
    if (!auth?.isAuthenticated) return null;

    const { primary, settings } = this.navItemsForUser();
    const role = auth.user?.role || 'client';
    const isTrainerPlus = hasMinimumRole(role, 'trainer');
    // Toggle visibility is gated on the server `canSelfTrack` flag (see
    // navItemsForUser); users who can't self-track hard-pin to 'trainer'.
    const canSelfTrack = canUseClientView(auth.user);
    const viewMode = resolveViewMode(auth.user, auth.viewMode);

    const { mobileOpen } = this.props;

    // Trainer / org logo override. When the resolved branding (set
    // by /api/auth) has a `logo` URL, render that single image in
    // place of the Nubod wordmark pair. Falls back to the Nubod
    // logos when branding has no logo set.
    //
    // Render the RAW uploaded URL (same as mobile + the Settings
    // preview). The `.brand-logo--custom` CSS already sizes it to a
    // uniform 5:1 footprint via `object-fit: contain` in the 140×28
    // frame, so no Cloudinary display-transform is needed. We used to
    // pad via `padLogoUrl`, but an on-the-fly Cloudinary transform
    // fails (broken-image icon) when the account restricts derived
    // transformations — even though the raw asset loads fine.
    const brandingLogo = auth.user?.branding?.logo || null;
    const brandingName = auth.user?.branding?.name || null;
    const brandLabel = brandingName || 'Nubod';

    return (
      <aside className={`sidebar ${mobileOpen ? 'mobile-open' : ''}`}>
        <div className="brand">
          {brandingLogo && !this.state.brandLogoFailed ? (
            <img
              src={brandingLogo}
              alt={brandLabel}
              className="brand-logo brand-logo--custom"
              // If the logo URL can't load (dead link, blocked host),
              // fall back to the Nubod wordmark instead of the browser's
              // broken-image icon. Reset in componentDidUpdate when the
              // logo URL changes so a fixed/replaced logo re-renders.
              onError={() => this.setState({ brandLogoFailed: true })}
            />
          ) : (
            <>
              <img
                src={NubodWordmarkBlack}
                alt="Nubod"
                className="brand-logo brand-logo--light"
              />
              <img
                src={NubodWordmarkWhite}
                alt="Nubod"
                className="brand-logo brand-logo--dark"
              />
            </>
          )}
          {/* Hide the "Trainer studio" / "Pro account" tag when a
              trainer/org has uploaded a custom logo — the logo is
              already the brand statement, and the redundant label
              under it looked cluttered. Falls back to showing the
              tag in the default Nubod-wordmark case where the
              extra context helps the user place themselves. */}
          {!brandingLogo && (
            <div className="brand-tag">
              {isTrainerPlus
                ? viewMode === 'client'
                  ? 'Trainer · client view'
                  : 'Trainer studio'
                : 'Pro account'}
            </div>
          )}
        </div>

        {/* View-mode toggle — platform_admin only for now. Lets us
            flip the sidebar between coach surfaces (Studio Overview,
            Clients, Library) and the client view to QA the app from
            both sides. Hidden for trainers and org-admins because
            they don't have a personal client experience and the
            toggle made the sidebar feel confusing. */}
        {/* Hidden in the collapsed icon rail — the two-up Trainer/Client
            segmented control has no room there and looked broken. It returns
            when the sidebar is expanded. */}
        {canSelfTrack && !this.props.collapsed ? (
          <div
            className="sidebar-mode-toggle"
            role="group"
            aria-label="View mode"
            style={{
              display: 'flex',
              gap: 4,
              padding: 4,
              margin: '0 12px 12px',
              background: 'var(--bg-soft)',
              borderRadius: 10,
            }}
          >
            <button
              type="button"
              onClick={() => this.handleToggleViewMode('trainer')}
              style={{
                flex: 1,
                border: 0,
                background:
                  viewMode === 'trainer'
                    ? 'var(--bg-surface)'
                    : 'transparent',
                color:
                  viewMode === 'trainer'
                    ? 'var(--fg)'
                    : 'var(--fg-muted)',
                fontWeight: 600,
                fontSize: 12,
                padding: '6px 8px',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow:
                  viewMode === 'trainer'
                    ? '0 1px 2px rgba(0,0,0,0.06)'
                    : 'none',
                transition:
                  'background 120ms ease, color 120ms ease',
              }}
            >
              Trainer
            </button>
            <button
              type="button"
              onClick={() => this.handleToggleViewMode('client')}
              style={{
                flex: 1,
                border: 0,
                background:
                  viewMode === 'client'
                    ? 'var(--bg-surface)'
                    : 'transparent',
                color:
                  viewMode === 'client'
                    ? 'var(--fg)'
                    : 'var(--fg-muted)',
                fontWeight: 600,
                fontSize: 12,
                padding: '6px 8px',
                borderRadius: 8,
                cursor: 'pointer',
                fontFamily: 'inherit',
                boxShadow:
                  viewMode === 'client'
                    ? '0 1px 2px rgba(0,0,0,0.06)'
                    : 'none',
                transition:
                  'background 120ms ease, color 120ms ease',
              }}
            >
              Client
            </button>
          </div>
        ) : null}

        {isTrainerPlus && viewMode === 'trainer' ? (
          <Dropdown
            trigger={['click']}
            placement="bottomLeft"
            visible={this.state.createMenuOpen}
            onVisibleChange={(v) => this.setState({ createMenuOpen: v })}
            overlay={
              <div className="nav-create-menu">
                {[
                  { key: 'client', label: 'New client', Icon: IcoUsers },
                  { divider: true },
                  { key: 'workout', label: 'New workout', Icon: IcoDumb },
                  { key: 'program', label: 'New program', Icon: IcoBook },
                  { key: 'mealplan', label: 'New meal plan', Icon: IcoApple },
                  { key: 'exercise', label: 'New exercise', Icon: IcoBolt },
                ].map((it, i) => {
                  if (it.divider) {
                    return <div key={`sep${i}`} className="nav-create-sep" />;
                  }
                  const Icon = it.Icon;
                  return (
                    <button
                      key={it.key}
                      type="button"
                      className="nav-create-item"
                      onClick={() => {
                        this.setState({ createMenuOpen: false });
                        this.handleQuickCreate(it.key);
                      }}
                    >
                      <Icon className="ico" size={16} />
                      <span>{it.label}</span>
                    </button>
                  );
                })}
              </div>
            }
          >
            <button type="button" className="nav-create-btn" data-tour="create">
              <IcoPlus className="ico" size={16} />
              <span>Create</span>
              <IcoChevR className="nav-create-chev" size={14} />
            </button>
          </Dropdown>
        ) : null}

        <div className="nav-section">
          {isTrainerPlus && viewMode === 'trainer' ? 'Coach' : 'Daily'}
        </div>
        {primary.map((it) => this.renderSection(it))}

        <div className="nav-section">More</div>
        {this.props.collapsed ? (
          <Tooltip placement="right" title="Messages">
            <div className="nav-group">
              <button
                type="button"
                className={`nav-item ${this.isActive('/dashboard/messages') ? 'active' : ''}`}
                onClick={() => this.navigate('/dashboard/messages')}
                aria-label="Messages"
                data-tour="messages"
              >
                <IcoMsg className="ico" size={20} />
                <span className="nav-item-label">Messages</span>
              </button>
            </div>
          </Tooltip>
        ) : (
          <button
            type="button"
            className={`nav-item ${this.isActive('/dashboard/messages') ? 'active' : ''}`}
            onClick={() => this.navigate('/dashboard/messages')}
            data-tour="messages"
          >
            <IcoMsg className="ico" size={18} />
            <span className="nav-item-label">Messages</span>
          </button>
        )}
        {/* Settings is intentionally omitted from the main sidebar
            — see `navItemsForUser`. Access lives in the user-avatar
            dropdown rendered by `renderUserCard()` below. */}
        {settings ? this.renderSection(settings) : null}

        {/* Onboarding "Get started" widget — uses the dead vertical
            space between the nav and the user-card. Renders only
            in trainer mode (clients don't have onboarding to do
            here); component auto-hides once all items complete or
            after dismissal. */}
        {isTrainerPlus && viewMode === 'trainer' ? (
          <div className="sidebar-onboarding-slot" data-tour="get-started">
            <OnboardingChecklist />
          </div>
        ) : null}

        {this.props.onToggleCollapse ? (
          <button
            type="button"
            className="sidebar-collapse-btn"
            onClick={this.props.onToggleCollapse}
            aria-label={this.props.collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={this.props.collapsed ? 'Expand' : 'Collapse'}
          >
            <IcoChevR
              className="ico"
              size={16}
              style={{ transform: this.props.collapsed ? 'none' : 'rotate(180deg)' }}
            />
            <span className="nav-item-label">Collapse</span>
          </button>
        ) : null}

        {this.renderUserCard()}
      </aside>
    );
  }
}

const menuItemStyle = {
  display: 'block',
  width: '100%',
  textAlign: 'left',
  border: 0,
  background: 'transparent',
  padding: '8px 10px',
  borderRadius: 8,
  fontSize: 13,
  color: 'var(--fg)',
  cursor: 'pointer',
  fontFamily: 'inherit',
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = {
  logOut,
  clearProfile,
  clearRecipes,
  setViewMode,
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Sidebar));
